import workflowDashboardImage from "@/assets/automations-execution.webp";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { FeatureItem } from "@/components/education/FeatureItem";
import { EducationPageContainer } from "@/components/education/EducationPageContainer";
import { ChangeSubscriptionModal } from "@/routes/settings/organization/billing/ChangeSubscriptionModal";
import { AlertTriangleIcon, GitBranchIcon, ShieldCheckIcon, PlusIcon } from "lucide-react";
import { type FC, useCallback, useState } from "react";
import { ScrollArea } from "@/components/flexkit/ScrollArea";

export const AutomationsEducationPage: FC = () => {
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const handleUpgradeModalClose = useCallback((open: boolean) => {
        setShowUpgradeModal(open);
    }, []);

    return (
        // Scroll handling & padding needed as layout doesn't include padding
        <div className="size-full p-0.5">
            <ScrollArea orientation="vertical" className="flex max-h-full flex-col gap-6 p-6">
                <EducationPageContainer className="">
                    <div className="flex flex-col gap-2">
                        <Pill variant="brand" size="lg" className="w-fit">
                            Available on Core and above ✨
                        </Pill>
                        <Heading1>Build automations that scale across every repo.</Heading1>
                        <Paragraph className="text-md text-content-secondary">
                            Chain agents and commands into multi-step Automations that run across your entire codebase -
                            scheduled or event driven.
                        </Paragraph>

                        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                            <Button
                                size="md"
                                onClick={() => setShowUpgradeModal(true)}
                                data-tracking-id="upgrade-to-core-automations-education-page"
                            >
                                Upgrade plan
                            </Button>
                            <Button variant="outline" size="md" asChild>
                                <ExternalLink href="https://ona.com/docs/ona/automations/overview" iconSize="sm">
                                    Learn more
                                </ExternalLink>
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                        {/* Image - shows first on mobile, second on desktop */}
                        <img
                            src={workflowDashboardImage}
                            alt="Ona Automations dashboard"
                            className="order-1 w-full rounded-lg shadow-xl lg:order-2 lg:w-[65%]"
                        />

                        {/* Features - shows second on mobile, first on desktop */}
                        <div className="order-2 flex flex-col gap-3 lg:order-1 lg:w-[35%]">
                            <Paragraph className="text-base font-medium text-content-primary">
                                Use automations to:
                            </Paragraph>

                            <FeatureItem
                                icon={<ShieldCheckIcon size={16} />}
                                variant="success"
                                title="Enforce Compliance and Governance"
                                description="Automate PR reviews and compliance checks with audit-ready reports and documentation."
                            />

                            <FeatureItem
                                icon={<AlertTriangleIcon size={16} />}
                                variant="warning"
                                title="Flag and fix vulnerabilities"
                                description="Automate scans, patching, and open pull requests for review - reducing remediation time to hours."
                            />

                            <FeatureItem
                                icon={<GitBranchIcon size={16} />}
                                variant="brand-purple"
                                title="Standardize and Migrate at Scale"
                                description="Upgrade language versions, unify configurations, or migrate infrastructure through a single defined window."
                            />

                            <FeatureItem
                                icon={<PlusIcon size={16} />}
                                variant="brand"
                                title="Or design your own Automation"
                                description="Let Ona handle the routines, so you can stay focused on building."
                            />
                        </div>
                    </div>

                    {showUpgradeModal && <ChangeSubscriptionModal onOpenChange={handleUpgradeModalClose} />}
                </EducationPageContainer>
            </ScrollArea>
        </div>
    );
};
